import { StickySetting } from 'client/components/core/Sidebar/components/Settings/entities/SectionSettings/components/StickySetting'
import { SectionInterface } from 'common/components/entities/Section/types/SectionInterface'
import {
  ContentWidthEnum,
  MarginType,
  PaddingType,
} from 'common/types/styleTypes'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { Repeat04Icon } from 'src/editor/shared/icons/repeat-04-icon'
import { MarginGroup } from 'src/editor/shared/settings/groups/margin'
import { PaddingGroup } from 'src/editor/shared/settings/groups/padding'
import {
  ButtonSizeEnum,
  GhostIconButton,
} from 'src/editor/shared/ui-lib/buttons'
import { ContentWidthControl } from '../../controls/content-width-control'

interface SizeAndPositionGroupProps {
  withoutHorizontalLine?: boolean
  title?: React.ReactNode
  margin?: Partial<MarginType> | MarginType | undefined
  defaultMargin?: Partial<MarginType> | MarginType | undefined
  updateMargin?: (
    margin: Partial<MarginType> | undefined,
    newEntity?: Record<string, any> | undefined,
  ) => Record<string, any>
  padding?: PaddingType | Partial<PaddingType> | undefined
  defaultPadding?: PaddingType | Partial<PaddingType> | undefined
  updatePadding?: (
    padding: PaddingType | Partial<PaddingType> | undefined,
    newEntity?: Record<string, any> | undefined,
  ) => Record<string, any>
  contentWidth?: ContentWidthEnum | undefined
  defaultContentWidth?: ContentWidthEnum | undefined
  updateContentWidth?: (
    contentWidth: ContentWidthEnum | undefined,
    newEntity?: Record<string, any> | undefined,
  ) => Record<string, any>
  sticky?: SectionInterface['sticky']
  defaultSticky?: SectionInterface['sticky']
  htmlAttrId?: string
  updateSticky?: (value: SectionInterface['sticky']) => void
  singleDeviceMode?: boolean
  stickyOptions?: {
    title?: string
    label?: string
    tooltipText?: string
    isShowSelect?: boolean
    singleDeviceMode?: boolean
  }
}

export const SizeAndPositionGroup = (props: SizeAndPositionGroupProps) => {
  const {
    title = 'settings_styles.size_and_position.label',
    updateMargin,
    margin,
    updatePadding,
    padding,
    updateContentWidth,
    contentWidth,
    updateSticky,
    sticky,
    htmlAttrId,
    singleDeviceMode,
    withoutHorizontalLine,
  } = props

  const isMarginPropPassed = 'margin' in props && !!updateMargin
  const isPaddingPropPassed = 'padding' in props && !!updatePadding
  const isContentWidth = 'contentWidth' in props && !!updateContentWidth
  const isSticky = 'sticky' in props && !!updateSticky && !!htmlAttrId
  const isDefaultStylesPassed =
    'defaultMargin' in props ||
    'defaultPadding' in props ||
    'defaultContentWidth' in props

  const onResetStyles = () => {
    let updatedEntity: Record<string, any> | undefined

    if (isMarginPropPassed) {
      updatedEntity = updateMargin(props.defaultMargin)
    }

    if (isPaddingPropPassed) {
      updatedEntity = updatePadding(props.defaultPadding, updatedEntity)
    }

    if (isContentWidth) {
      updateContentWidth(props.defaultContentWidth, updatedEntity)
    }
  }

  return (
    <>
      <GroupTitle
        withoutHorizontalLine={withoutHorizontalLine}
        afterChildren={
          isDefaultStylesPassed && (
            <GhostIconButton
              size={ButtonSizeEnum.Small}
              onClick={onResetStyles}
              icon={<Repeat04Icon />}
            />
          )
        }
      >
        {title}
      </GroupTitle>
      {isContentWidth && (
        <ContentWidthControl
          contentWidth={contentWidth}
          update={updateContentWidth}
        />
      )}
      {isPaddingPropPassed && (
        <PaddingGroup
          padding={padding}
          update={updatePadding}
          singleDeviceMode={singleDeviceMode}
        />
      )}
      {isMarginPropPassed && (
        <MarginGroup
          margin={margin}
          update={updateMargin}
          singleDeviceMode={singleDeviceMode}
        />
      )}
      {isSticky && (
        <StickySetting
          entityHtmlAttrId={htmlAttrId}
          sticky={sticky}
          update={updateSticky}
          title={props.stickyOptions?.title}
          label={props.stickyOptions?.label}
          tooltipText={props.stickyOptions?.tooltipText}
          isShowSelect={props.stickyOptions?.isShowSelect}
          singleDeviceMode={props.stickyOptions?.singleDeviceMode}
        />
      )}
    </>
  )
}
